'use client'

import HealthMapForm, {
    countAnswered,
    draftFromAnswers,
    draftToAnswers,
    HealthMapDraft,
} from '@/components/health-map/health-map-form'
import {
    getPublicHealthMapMeta,
    HealthMapCategory,
    resumePublicHealthMap,
    savePublicHealthMapAnswers,
    startPublicHealthMap,
    submitPublicHealthMap,
} from '@/lib/api/health-map/health-map-api'
import { useCallback, useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdArrowForward,
    MdBadge,
    MdCheckCircle,
    MdLockOutline,
    MdMonitorHeart,
} from 'react-icons/md'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

/** 000.000.000-00 — mesma máscara do link do procedimento. */
const formatCpf = (value: string): string => {
    const n = onlyDigits(value).slice(0, 11)
    if (n.length <= 3) return n
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9, 11)}`
}

/**
 * Sessão por LINK no sessionStorage: abrir outro link não aparece autenticado, e
 * a sessão morre ao fechar a aba (o celular costuma ser compartilhado). Guardamos
 * o JWT, NUNCA o CPF.
 */
const sessionKey = (token: string): string => `health-map-session:${token}`

const INPUT_CLASS =
    'w-full h-[52px] pl-12 pr-4 rounded-2xl border border-base-200 bg-base-100 text-[15px] text-base-content placeholder:text-base-content/40 outline-none transition-colors duration-200 focus:border-secondary focus:ring-4 focus:ring-secondary/12'

const PRIMARY_BTN =
    'w-full h-[52px] rounded-2xl bg-secondary text-primary-content font-semibold text-[15px] transition-all duration-300 hover:brightness-[1.05] hover:shadow-lg hover:shadow-secondary/25 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none'

/**
 * Mapa da Saúde respondido pelo paciente (`/health-map/:token`), SEM login.
 *
 * O paciente confirma o CPF e responde no próprio celular. Uma categoria por vez,
 * com salvamento a cada avanço: o questionário é longo, quem responde na fila do
 * ônibus não termina de uma vez, e perder o que já foi marcado é o que faz o
 * paciente desistir — e o médico não receber nada.
 */
export default function PublicHealthMapPage() {
    const { token } = useParams<{ token: string }>()

    const [metaLoading, setMetaLoading] = useState(true)
    const [invalidLink, setInvalidLink] = useState(false)
    const [companyName, setCompanyName] = useState<string | null>(null)

    const [cpf, setCpf] = useState('')
    const [verifying, setVerifying] = useState(false)
    const [accessToken, setAccessToken] = useState<string | null>(null)

    const [clientName, setClientName] = useState('')
    const [categories, setCategories] = useState<HealthMapCategory[]>([])
    const [draft, setDraft] = useState<HealthMapDraft>({})
    const [categoryIndex, setCategoryIndex] = useState(0)
    const [saving, setSaving] = useState(false)
    const [finished, setFinished] = useState(false)

    const applyView = useCallback(
        (view: {
            clientName: string
            companyName: string | null
            structure: HealthMapCategory[]
            response: { answers: any[] }
        }) => {
            setClientName(view.clientName)
            setCompanyName(view.companyName)
            setCategories(view.structure)
            setDraft(draftFromAnswers(view.response.answers as any))
        },
        []
    )

    const bootstrap = useCallback(async () => {
        if (!token) return
        setMetaLoading(true)
        try {
            const meta = await getPublicHealthMapMeta(token)
            if (!meta.success || !meta.result) {
                setInvalidLink(true)
                return
            }
            setCompanyName(meta.result.companyName)

            // Retoma a sessão do próprio link, se ainda vale — o paciente que
            // voltou não redigita o CPF.
            const saved = sessionStorage.getItem(sessionKey(token))
            if (saved) {
                const resumed = await resumePublicHealthMap(token, saved)
                if (resumed.success && resumed.result) {
                    setAccessToken(saved)
                    applyView(resumed.result)
                } else {
                    sessionStorage.removeItem(sessionKey(token))
                }
            }
        } catch {
            setInvalidLink(true)
        } finally {
            setMetaLoading(false)
        }
    }, [token, applyView])

    useEffect(() => {
        bootstrap()
    }, [bootstrap])

    const handleVerify = async () => {
        if (!token) return
        if (onlyDigits(cpf).length !== 11) {
            toast.error('Digite o CPF completo')
            return
        }

        setVerifying(true)
        try {
            const result = await startPublicHealthMap(token, onlyDigits(cpf))
            if (result.success && result.result) {
                sessionStorage.setItem(
                    sessionKey(token),
                    result.result.accessToken
                )
                setAccessToken(result.result.accessToken)
                applyView(result.result)
            } else {
                toast.error(result.error || 'Não foi possível abrir o questionário')
            }
        } catch {
            toast.error('Não foi possível abrir o questionário')
        } finally {
            setVerifying(false)
        }
    }

    /** Grava o que já foi respondido. Silencioso: é salvamento de rascunho. */
    const persist = async (): Promise<boolean> => {
        if (!token) return false
        const answers = draftToAnswers(draft)
        if (answers.length === 0) return true

        const result = await savePublicHealthMapAnswers(
            token,
            { accessToken: accessToken ?? undefined, cpf: onlyDigits(cpf) },
            answers
        )
        if (!result.success) {
            toast.error(result.error || 'Não foi possível salvar suas respostas')
            return false
        }
        return true
    }

    const handleNext = async () => {
        setSaving(true)
        try {
            if (!(await persist())) return
            setCategoryIndex(index => Math.min(index + 1, categories.length - 1))
            window.scrollTo({ top: 0, behavior: 'smooth' })
        } finally {
            setSaving(false)
        }
    }

    const handleFinish = async () => {
        if (!token) return
        setSaving(true)
        try {
            if (!(await persist())) return
            const result = await submitPublicHealthMap(token, {
                accessToken: accessToken ?? undefined,
                cpf: onlyDigits(cpf),
            })
            if (result.success) {
                sessionStorage.removeItem(sessionKey(token))
                setFinished(true)
                window.scrollTo({ top: 0, behavior: 'smooth' })
            } else {
                toast.error(result.error || 'Não foi possível enviar suas respostas')
            }
        } finally {
            setSaving(false)
        }
    }

    if (metaLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-base-200/40">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (invalidLink) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-base-200/40 p-6 text-center">
                <MdLockOutline className="text-5xl text-base-content/30" />
                <h1 className="text-xl font-bold">Link inválido ou expirado</h1>
                <p className="max-w-sm text-sm text-base-content/60">
                    Peça um novo link à clínica para responder o Mapa da Saúde.
                </p>
            </div>
        )
    }

    if (finished) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-base-200/40 p-6 text-center">
                <MdCheckCircle className="text-6xl text-success" />
                <h1 className="text-2xl font-bold">Respostas enviadas</h1>
                <p className="max-w-sm text-sm text-base-content/60">
                    Obrigado, {clientName || 'paciente'}! Sua equipe de saúde já
                    tem acesso ao que você respondeu.
                </p>
            </div>
        )
    }

    // Portão de CPF
    if (!accessToken) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-base-200/40 p-6">
                <div className="w-full max-w-md rounded-3xl border border-base-200 bg-base-100 p-8 shadow-sm">
                    <div className="mb-6 text-center">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary/10">
                            <MdMonitorHeart className="text-3xl text-secondary" />
                        </div>
                        <h1 className="text-xl font-bold">Mapa da Saúde</h1>
                        {companyName && (
                            <p className="text-sm text-base-content/60">
                                {companyName}
                            </p>
                        )}
                        <p className="mt-3 text-sm text-base-content/60">
                            Confirme seu CPF para responder o questionário.
                        </p>
                    </div>

                    <div className="relative mb-4">
                        <MdBadge className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-xl text-base-content/40" />
                        <input
                            type="tel"
                            inputMode="numeric"
                            className={INPUT_CLASS}
                            placeholder="000.000.000-00"
                            value={cpf}
                            onChange={event =>
                                setCpf(formatCpf(event.target.value))
                            }
                            onKeyDown={event => {
                                if (event.key === 'Enter') handleVerify()
                            }}
                        />
                    </div>

                    <button
                        type="button"
                        className={PRIMARY_BTN}
                        onClick={handleVerify}
                        disabled={verifying}
                    >
                        {verifying ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            'Começar'
                        )}
                    </button>

                    <p className="mt-4 text-center text-xs text-base-content/50">
                        Suas respostas são confidenciais e ficam disponíveis
                        apenas para a equipe que cuida de você.
                    </p>
                </div>
            </div>
        )
    }

    const category = categories[categoryIndex]
    const progress = countAnswered(categories, draft)
    const isLast = categoryIndex === categories.length - 1

    return (
        <div className="min-h-screen bg-base-200/40 pb-28">
            <header className="border-b border-base-200 bg-base-100 px-4 py-4">
                <div className="mx-auto flex max-w-2xl items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10">
                        <MdMonitorHeart className="text-xl text-secondary" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h1 className="truncate text-base font-bold">
                            Mapa da Saúde
                        </h1>
                        <p className="truncate text-xs text-base-content/60">
                            {clientName}
                            {companyName ? ` · ${companyName}` : ''}
                        </p>
                    </div>
                    <span className="text-xs text-base-content/60">
                        {progress.answered}/{progress.total}
                    </span>
                </div>
            </header>

            <main className="mx-auto flex max-w-2xl flex-col gap-5 p-4">
                {category && (
                    <>
                        <div>
                            <p className="text-xs uppercase tracking-wide text-base-content/50">
                                Etapa {categoryIndex + 1} de {categories.length}
                            </p>
                            <h2 className="text-lg font-bold text-base-content">
                                {category.name}
                            </h2>
                            {category.description && (
                                <p className="text-sm text-base-content/60">
                                    {category.description}
                                </p>
                            )}
                        </div>

                        <HealthMapForm
                            category={category}
                            draft={draft}
                            onChange={setDraft}
                        />
                    </>
                )}
            </main>

            {/* Barra de navegação fixa: no celular, o botão de avançar não pode
                depender de rolar até o fim de uma categoria com 30 perguntas. */}
            <div className="fixed inset-x-0 bottom-0 border-t border-base-200 bg-base-100 p-4">
                <div className="mx-auto flex max-w-2xl items-center gap-3">
                    {categoryIndex > 0 && (
                        <button
                            type="button"
                            className="btn btn-ghost gap-2"
                            onClick={() => {
                                setCategoryIndex(index => index - 1)
                                window.scrollTo({ top: 0, behavior: 'smooth' })
                            }}
                            disabled={saving}
                        >
                            <MdArrowBack className="text-lg" />
                            Voltar
                        </button>
                    )}
                    <button
                        type="button"
                        className={`${PRIMARY_BTN} flex items-center justify-center gap-2`}
                        onClick={isLast ? handleFinish : handleNext}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : isLast ? (
                            <>
                                <MdCheckCircle className="text-lg" />
                                Enviar respostas
                            </>
                        ) : (
                            <>
                                Continuar
                                <MdArrowForward className="text-lg" />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}
